import React, { useState } from 'react'
import { Box, ButtonBase, Popover, Typography } from '@mui/material'
import { advancedColors, surfaces } from '../../theme'
import { roleLine } from './roleLine'
import { useCareTeamRoster } from './useCareTeamRoster'

const CareTeamChatParticipants = ({ uid }: { uid: string }) => {
  const { roster } = useCareTeamRoster(uid, true)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const id = `care-team-chat-participants-${uid}`

  return (
    <>
      <ButtonBase
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        aria-controls={anchor ? id : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          flexShrink: 0,
          px: 0.5,
          py: 0.75,
          color: surfaces.blue.fg,
          borderRadius: '4px',
          '&:hover, &.Mui-focusVisible': { backgroundColor: surfaces.blue.bg },
        }}
      >
        <Typography variant="caption" sx={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}>
          {roster.length} participant{roster.length === 1 ? '' : 's'}
        </Typography>
      </ButtonBase>
      <Popover
        id={id}
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          role: 'dialog',
          'aria-label': 'Chat participants',
          sx: { p: 2, width: 320, maxWidth: 'calc(100vw - 32px)', maxHeight: 'min(360px, 70vh)' },
          onKeyDown: (event) => {
            if (event.key === 'Escape') {
              event.stopPropagation()
              setAnchor(null)
            }
          },
        }}
      >
        <Typography component="h3" variant="subtitle2" sx={{ color: surfaces.blue.fg }}>
          Participants
        </Typography>
        <Box
          component="ul"
          aria-label="Chat participants"
          sx={{
            display: 'grid',
            gap: 1.25,
            listStyle: 'none',
            m: 0,
            mt: 1,
            p: 0,
          }}
        >
          {roster.map((member) => (
            <Box component="li" key={member.id} sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: advancedColors.inkMuted, overflowWrap: 'anywhere' }}
              >
                {member.name}
              </Typography>
              {roleLine(member.name, member.role) && (
                <Typography variant="caption" component="div" sx={{ color: advancedColors.inkFaint }}>
                  {member.role}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  )
}

export default CareTeamChatParticipants
